import React from "react";
import Markdown from "react-markdown";

interface VaultArticleProps {
  title: string;
  documentId?: string;
  effectiveDate?: string;
  /** Policy record description field — shown on the LEFT */
  policyDescription?: string;
  /** Rendered content from the linked Document record — shown on the RIGHT */
  documentContent?: string;
  /** Whether document content is still loading */
  documentLoading?: boolean;
  /** Heading for the right-side content area (e.g. "Obligations — GDPR Notice") */
  mainHeading?: string;
  /** Legacy props for backward compat */
  sidebarContent?: string;
  mainContent?: string;
  sections?: Array<{ heading: string; content: string }>;
}

export const VaultArticle: React.FC<VaultArticleProps> = ({
  title,
  documentId,
  effectiveDate,
  policyDescription,
  documentContent,
  documentLoading = false,
  mainHeading,
  // Legacy fallbacks
  sidebarContent,
  mainContent,
  sections = [],
}) => {
  // Split title into lines for display typography
  const titleLines = title.toUpperCase().split(/\s+/);
  const midpoint = Math.ceil(titleLines.length / 2);
  const line1 = titleLines.slice(0, midpoint).join(" ");
  const line2 = titleLines.slice(midpoint).join(" ");

  // Resolve left content: prefer policyDescription, fallback to legacy sidebarContent
  const leftContent = policyDescription || sidebarContent || "";
  // Resolve right content: prefer documentContent, fallback to legacy mainContent
  const rightContent = documentContent || mainContent || "";

  /** Render text content — use Markdown by default, only fall back to raw HTML
   *  for content that looks like a full HTML document (starts with doctype/html tag) */
  const isFullHtml = (s: string) => /^\s*(<(!doctype|html)\b)/i.test(s);
  const renderContent = (text: string) =>
    isFullHtml(text) ? (
      <div dangerouslySetInnerHTML={{ __html: text }} />
    ) : (
      <Markdown>{text}</Markdown>
    );

  return (
    <div className="vault-article-scroll-area">
      {/* Crosshair decoration */}
      <div
        className="vault-crosshair"
        style={{ top: "var(--vault-spacing-lg)", left: "50%" }}
      />

      <header className="vault-article-header">
        <div className="vault-article-title-area">
          <h1 className="vault-heading-display">
            {line1}
            {line2 && (
              <>
                <br />
                {line2}
              </>
            )}
          </h1>
        </div>
        {(documentId || effectiveDate) && (
          <div className="vault-article-meta-row">
            {documentId && (
              <div className="vault-meta-block">
                <span className="vault-label-tiny">Document ID</span>
                <span className="vault-text-small">{documentId}</span>
              </div>
            )}
            {effectiveDate && (
              <div className="vault-meta-block vault-meta-block-right">
                <span className="vault-label-tiny">Effective Date</span>
                <span className="vault-text-small">{effectiveDate}</span>
              </div>
            )}
          </div>
        )}
      </header>

      <div className="vault-article-body" style={{ gridTemplateColumns: "1fr" }}>
        <div className="vault-article-main-text vault-text-body-editorial">
          {leftContent && (
            <div style={{ color: "var(--vault-fg-muted)", marginBottom: "var(--vault-spacing-md)", paddingBottom: "var(--vault-spacing-md)", borderBottom: "1px solid var(--vault-grid-line)" }}>
              {renderContent(leftContent)}
            </div>
          )}
          {mainHeading && (
            <h2 className="vault-heading-editorial">{mainHeading}</h2>
          )}
          {documentLoading ? (
            <p className="vault-text-muted">Loading document content...</p>
          ) : rightContent ? (
            renderContent(rightContent)
          ) : !leftContent ? (
            <p className="vault-text-muted">
              Seleziona un documento dalla barra laterale per visualizzarne il contenuto.
            </p>
          ) : null}
          {sections.map((section, idx) => (
            <React.Fragment key={idx}>
              <h3>{section.heading}</h3>
              {renderContent(section.content)}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};
